// =====================================================================
// CommandePreteEditeur.jsx — Commandes prêtes (13-10-2026, v2 multi-client 16-10-2026) — L'ÉCRAN PDA
// =====================================================================
// Reproduit le comportement de la maquette validée par Silviu
// (brain-docs-day8/nespart-commande-prete/flux-commande-prete.html, section PDA) :
// mêmes identifiants d'éléments (#ord, #ref, #npal, #ready, #readyNote, .card,
// sscc-<id>, h-<id>, php-<id>…), mêmes messages, même navigation Entrée/scan.
// La logique PURE est dans pages/commande-prete/*.js (testée par node --test).
//
// Différences avec la maquette :
//   - les données vivent sur le serveur (/api/ready-orders, CommandeDTO) : chaque
//     champ est enregistré à la sortie du champ / Entrée / Scan, une requête à la
//     fois (file d'attente : les réponses arrivent dans l'ordre), avec un état par
//     fiche (enregistrement… / enregistrée ✓ / non enregistrée — touchez pour
//     réessayer). L'envoi attend la fin des enregistrements et refuse s'il en
//     reste un en erreur ;
//   - les messages de saisie du PDA s'affichent AVANT l'appel serveur ; une valeur
//     fausse n'est jamais envoyée, une valeur refusée par le serveur (SSCC d'une
//     autre commande…) est marquée sur la fiche comme une valeur fausse ;
//   - (a) UN seul identifiant en haut (14/10) : « Réf. chargement (ODP) » (#ord), ex.
//     BROUT117200. Entrée/scan valide le champ et passe au compteur de palettes s'il n'y a
//     aucune fiche, sinon au premier champ à faire : le scan SSCC suivant ne s'ajoute jamais
//     à la référence ;
//   - une fiche n'envoie que les champs modifiés SUR CET APPAREIL (champs « sales ») et adopte
//     les autres valeurs du serveur : un champ vide ici n'efface jamais une saisie d'un autre
//     appareil. Supprimer une fiche remplie demande une confirmation explicite au serveur ;
//   - pendant un envoi (en cours / à vérifier), le serveur refuse toute écriture : la saisie reste
//     à l'écran, « non enregistrée — touchez pour réessayer » ;
//   - (b) photo OBLIGATOIRE : quand plus rien n'est à saisir, Entrée/scan et le
//     bouton d'envoi mènent à la fiche à laquelle il ne manque que la photo
//     (message + bouton 📷 mis en évidence) ;
//   - (c) SSCC en double : les DEUX fiches affichent l'alerte.
// Le focus ne va JAMAIS sur un bouton destructif ou d'envoi.
//
// v2 multi-client (16-10-2026, SPEC-v2 §1.2) — décision Silviu : « PDA encodage vreau să
// rămână la fel cum este, doar câmpul client să apară în plus ». L'écran est IDENTIQUE ; seuls
// s'ajoutent, tout en haut, le champ « Client » (ChoixClient, #cli) et la case « Commande de
// test » (#isTest), enregistrés aussitôt (PATCH clientId / isTest) et figés après le 1er mail
// comme la réf. Décision Silviu 17/10 : la case est réservée aux ADMINISTRATEURS (`admin`) — un
// opérateur n'a pas la case ; il encode la commande de test qu'on lui a confiée (badge TEST), réf. et
// client figés pour lui (#testEnteteNote), sans pouvoir la supprimer. (Réf. qui a aussi une commande de
// test : le choix essai / vraie commande se fait AVANT, sur la page Commandes prêtes — #cp-choix-test.)
// Après un choix de client, le curseur va à la réf. si elle manque, sinon à la suite (comme
// après la réf.) : un scan d'étiquette n'écrase jamais une réf. déjà posée.
//
// « Supprimer le brouillon » (16-10-2026, décision Silviu) : en bas de l'écran, seulement quand le
// serveur le permet (dto.supprimable : brouillon jamais envoyé, ou commande de test) ; question SUR LA
// PAGE (#supprBox), focus sur « Annuler » ; supprimée → retour à la liste, qui le dit.
//
// Droits PROPRES au module (SPEC v3 D, 17/10 — ./commande-prete/droits.js) : l'écran s'ouvre avec « Voir les
// commandes prêtes » ; sans « Encoder et envoyer », il est en LECTURE SEULE — tout le <fieldset> désactivé
// (aucune saisie, aucun envoi, aucune suppression ; les photos s'ouvrent encore) et le bandeau #lectureSeule
// le dit. `admin` = commandes_pretes.admin.all (case test, envoi « à vérifier »). « Vu » / « Imprimer » des
// rendez-vous et documents : « Imprimer les documents ».
// =====================================================================

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { readyOrders, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import ChoixClient from './commande-prete/ChoixClient.jsx';
import BadgeTest from './commande-prete/BadgeTest.jsx';
import { RdvDocsCommande } from './commande-prete/RdvDocsZeus.jsx';   // v3 B (16-10-2026)
import { useClientsCommandesPretes } from './commande-prete/useClients.js';
import {
  texteRegles, texteVerrouEntete, texteDejaChargee, MSG_ENTETE_TEST_ADMIN, MSG_CLIENT_TEST_ADMIN, MSG_CLIENT_TEST_ADMIN_VERROU,
  texteClientManqueTest,
} from './commande-prete/clients.js';
import { reduirePhoto } from '../lib/photos.js';
import { normSscc, MSG_ETIQ_PAL_SUR_COLIS, MSG_ETIQ_COLIS_SUR_PAL } from './commande-prete/sscc.js';
import {
  champs, formatEtat, isEmpty, MAX_UNITS, MAX_PHOTOS,
  normRefChargement, verifRefChargement, refInhabituelle, MSG_REF_INHABITUELLE,
} from './commande-prete/regles.js';
import {
  ID_NOTE, idPhoto, ofType, frName, setSscc, valider, poserRef, enAttente, stateOf, complete,
  cibleAllerA, cibleAvancer, cibleBloqueur, entreeFausse, blockers, bloqueursRef, sansBloqueursRef, texteACompleter,
  listeCourte, noteEssaiFige, texteRecap,
  planCompteur, videsARetirer, msgToutesRemplies, texteQuestionCompteur, texteQuestionAnnulee,
  manquePhoto, avertFiche, optionsSuppression, msgRempliAilleurs, abregeSscc,
} from './commande-prete/fiches.js';
import {
  diffFiche, diffEntete, fusionner, enteteDepuisServeur, extraireDto, salir, champEnregistre,
  saisiesNonEnregistrees, refusEnvoiFige, salirEntete, enteteSale, enteteEnregistre, adopterEntete,
  retirerFiche, remettreFiche,
} from './commande-prete/synchro.js';
import {
  peutSupprimer, libelleSuppression, texteConfirmationSuppression, texteSupprimee, texteRefusSuppression,
} from './commande-prete/suppression.js';
import { useGardeFocus } from './commande-prete/useGardeFocus.js';
import { droitsCommandesPretes, texteAccesRefuse, DROIT_LIRE, MSG_LECTURE_SEULE, NOTE_ENVOI_LECTURE_SEULE } from './commande-prete/droits.js';

// Champ du corps PATCH → champ de la fiche à l'écran.
const CHAMP_LOCAL = { sscc: 'sscc', heightCm: 'h', lengthCm: 'l', widthCm: 'w', weightKg: 'kg' };
const LIBELLES = { h: 'Hauteur (cm)', kg: 'Poids (kg)', l: 'Longueur (cm)', w: 'Largeur (cm)' };

const heure = (d) => new Date(d).toLocaleTimeString('fr-BE', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit' });
const dateHeure = (d) => new Date(d).toLocaleString('fr-BE', {
  timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).replace(' ', ' à ');

// Écriture refusée par le serveur (4xx : message humain, inutile de réessayer tel quel) ;
// sinon (pas de réponse, 5xx) : problème de réseau → « touchez pour réessayer ».
// Exception : commande FIGÉE par un envoi (409 ENVOI_EN_COURS / ENVOI_A_VERIFIER) — la valeur
// n'est pas fausse, elle attend : traitée comme un réseau coupé (réessayer après l'envoi).
const donnees = (e) => e?.response?.data || {};
const refusServeur = (e) => !!e?.response && e.response.status >= 400 && e.response.status < 500 && !refusEnvoiFige(donnees(e));

// Un champ rempli touché au doigt ou atteint par Entrée/Scan : sa valeur est sélectionnée,
// donc la frappe ou le scan la REMPLACE (sinon « 140 » + « 152 » = « 140152 »). Le premier
// appui ne place pas de curseur : il donne le focus et sélectionne tout.
function selectOnFocus(e) { e.target.select(); }
function keepSelection(e) { const t = e.target; if (document.activeElement !== t) { e.preventDefault(); t.focus(); t.select(); } }
// Entrée (fin de scan) sur un bouton 📷 / − : ne déclenche rien.
const sansEntree = (e) => { if (e.key === 'Enter') e.preventDefault(); };

const etatVide = () => ({
  units: [], tic: 0, format: 'EUR', fl: '120', fw: '80', ord: '', dto: null,   // ord = réf. de chargement (ODP)
  refusEntete: {}, vuEntete: {}, lienExistant: null,
  salesEntete: new Set(),               // en-tête changé SUR CET APPAREIL : 'ord' | 'fmt'
  countAsk: null, countMsg: '', countInfo: '', countInfoType: null,
  cnt: { PAL: null, COL: null },        // frappe en cours dans un compteur (null = affiche le nombre)
  cible: {},                            // nombre visé par un changement de compteur en cours
  ajoutEnCours: false,                  // « ＋ Palette / ＋ Colis » en attente du serveur
  armed: null, armTimer: 0, phase: 'edit',
  supprimees: new Set(),                // fiches retirées à l'écran, suppression pas encore confirmée
  photosMasquees: new Set(),            // photos retirées (« − »), effacement pas encore confirmé
  photosEchec: new Map(),               // fiche → photos non envoyées (réseau), gardées pour réessayer
  sauv: new Map(),                      // fiche → { n: écritures en cours, erreur, ok }
  sauvEntete: { n: 0, erreur: null, ok: false },
  enFileFiche: new Set(), enFileEntete: false,
  attente: new Set(),                   // fiches dont le SSCC attend qu'une autre le libère côté serveur
  envoi: { etat: 'repos', apercu: null, erreur: null, seq: 0 }, attenteRecap: false,
  message: null, avertEnvoi: [],
  // v2 : client et commande de test (enregistrés aussitôt, figés après le 1er mail)
  clientEnCours: null, refusClient: null, lienExistantClient: null, clientSeq: 0,
  testEnCours: null, refusTest: null, testSeq: 0,
  // « Supprimer le brouillon » : question ouverte, suppression en cours
  supprQuestion: false, supprEnCours: false,
});

// La page est remontée à chaque changement d'id (état neuf, file d'attente neuve).
export default function CommandePreteEditeur() {
  const { id } = useParams();
  return <Editeur key={id} id={id} />;
}

function Editeur({ id }) {
  const { can } = useAuth();
  const droits = droitsCommandesPretes(can);
  const autorise = droits.lire;
  const encoder = droits.encoder;
  // Sans « Encoder et envoyer » : écran en LECTURE SEULE (fieldset désactivé + bandeau #lectureSeule).
  const lectureSeule = !encoder;
  const admin = droits.admin;   // aussi : « Commande de test » (case, réf./client, suppression — 17/10)
  // Commande de test, sans le droit : réf. et client figés pour cet écran (le serveur refuse : 403 TEST_RESERVE_ADMIN).
  const testFige = (d) => !admin && !!d?.isTest;
  const location = useLocation();
  const navigate = useNavigate();

  // État de l'écran : un objet mutable (comme `st` dans la maquette) + un compteur de rendu.
  const sr = useRef(null);
  if (!sr.current) sr.current = etatVide();
  const st = sr.current;
  const [, setTick] = useState(0);
  const maj = useCallback(() => setTick((t) => t + 1), []);
  const [chargement, setChargement] = useState({ etat: 'charge', msg: '' });
  const [repris] = useState(() => !!location.state?.existing);
  // v2 : avertissements de la réponse de création (« commande existante : c'est / ce n'est PAS une commande
  // de TEST »), montrés tant que la case « test » demandée diffère de celle de la commande (rechargement
  // compris) ; ceux que la lecture de la commande redonne déjà ne sont pas doublés.
  const [avertCreation] = useState(() => ({
    liste: Array.isArray(location.state?.avertissements) ? location.state.avertissements : [],
    testDemande: typeof location.state?.testDemande === 'boolean' ? location.state.testDemande : null,
  }));
  const { clients, erreur: erreurClients } = useClientsCommandesPretes(autorise);   // propositions du champ Client (v2)

  const file = useRef(Promise.resolve());   // écritures serveur, une à la fois
  const enFile = useRef(0);
  const aFocaliser = useRef(null);
  const askSeq = useRef(0);
  const askVu = useRef(null);

  // ---------- Focus : appliqué après le rendu (les fiches nouvelles existent alors) ----------
  const focaliser = (cible, defiler = true) => { aFocaliser.current = { id: cible, defiler }; maj(); };
  useLayoutEffect(() => {
    const f = aFocaliser.current;
    if (!f) return;
    aFocaliser.current = null;
    const el = document.getElementById(f.id);
    if (!el) return;
    el.focus({ preventScroll: true });
    if (el.tagName === 'INPUT' && typeof el.select === 'function') el.select();
    if (f.defiler === 'center') el.scrollIntoView({ block: 'center' });
    else if (f.defiler) el.scrollIntoView({ block: 'nearest' });
  });
  // (revue 17/10, 12e-14e passes) Garde du focus (useGardeFocus, au niveau du document), après le focus voulu
  // ci-dessus : l'élément focalisé de l'écran vient de disparaître ou d'être désactivé — lui-même, ou par le
  // <fieldset> pendant un envoi / une suppression — sans que l'écran ait placé le focus ailleurs (« Supprimer ? »
  // fermée toute seule ou après un refus, bouton « réessayer » parti, #ord / #cli figés quand la commande devient un
  // essai, « ✓ Vu » / « Marquer imprimé » des rendez-vous et documents…) → la note du bouton d'envoi (Entrée n'y fait
  // rien), comme « Annuler » : le scan suivant n'est jamais perdu. Jamais volé à un champ actif en saisie.
  const refGarde = useGardeFocus(() => focaliser(ID_NOTE, false));

  // ---------- File d'attente des écritures ----------
  const enfiler = (tache) => {
    enFile.current += 1;
    const p = file.current
      .then(() => tache())
      .catch((e) => { console.error('[commande-prete]', e); return false; })
      .finally(() => { enFile.current -= 1; maj(); });
    file.current = p;
    maj();
    return p;
  };
  // Réponse du serveur → écran (les frappes en cours restent ; seules les photos viennent du serveur).
  const appliquer = (r) => {
    const dto = extraireDto(r);
    if (!dto || (st.dto && dto.id !== st.dto.id)) return null;
    fusionner(st, dto, st.supprimees, st.photosMasquees);
    // Champs d'en-tête non changés ici ; verrouillée, ou commande de test sans le droit (devenue telle
    // entre-temps compris) : la réf. du serveur fait foi — plus de frappe, de refus ni de lien en attente.
    adopterEntete(st, dto, { refFigee: testFige(dto) });
    // (10e revue) « Supprimer ? » ouverte mais plus permise (devenue un essai, sans le droit) : FERMÉE — jamais
    // réaffichée toute seule plus tard, déjà sur « Oui, supprimer ». (Le focus, s'il était sur un de ses boutons :
    // le garde du focus.)
    if (st.supprQuestion && !peutSupprimer(dto, admin, encoder)) st.supprQuestion = false;
    return dto;
  };
  const statutFiche = (uid) => {
    let s = st.sauv.get(uid);
    if (!s) { s = { n: 0, erreur: null, ok: false }; st.sauv.set(uid, s); }
    return s;
  };

  // ---------- Chargement ----------
  useEffect(() => {
    let actif = true;
    readyOrders.get(id).then((dto) => {
      if (!actif) return;
      fusionner(st, dto);
      enteteDepuisServeur(st, dto);
      setChargement({ etat: 'ok', msg: '' });
      // La réf. de chargement a ouvert la commande : premier scan attendu = le nombre d'unités, puis les fiches.
      focaliser(!st.units.length ? 'npal' : cibleAllerA(st, st.units));
    }).catch((e) => {
      if (actif) setChargement({ etat: 'erreur', msg: errMsg(e), introuvable: e?.response?.status === 404 });
    });
    return () => { actif = false; clearTimeout(st.armTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---------- Toute modification : ferme les questions ouvertes ----------
  const tourDepuis = (k) => [...st.units.slice(k), ...st.units.slice(0, k)];
  // Une question « Passer de X à Y ? » fermée par une autre action laisse une trace.
  const fermerQuestionCompteur = () => {
    const a = st.countAsk; st.countAsk = null;
    // Le message reste jusqu'à la prochaine action sur le nombre d'unités.
    if (a) { st.countInfoType = a.type; st.countInfo = texteQuestionAnnulee(a); }
  };
  const disarm = () => {
    if (!st.armed) return;
    const aid = st.armed; st.armed = null; clearTimeout(st.armTimer);
    // Jamais sur ✕ : l'Entrée du scan suivant rouvrirait la question de suppression.
    const a = document.activeElement;
    if (a && (a.id === 'del-ok-' + aid || a.id === 'del-no-' + aid)) {
      const k = st.units.findIndex((x) => x.id === aid);
      focaliser(cibleAllerA(st, k >= 0 ? tourDepuis(k) : st.units), false);
    }
  };
  const touched = () => {
    disarm();
    if (st.phase === 'confirm') st.phase = 'edit';
    st.countMsg = '';
    st.message = null;
    fermerQuestionCompteur();
    st.supprQuestion = false;   // une seule question ouverte : toute autre action ferme « Supprimer le brouillon ? »
  };

  // ---------- Enregistrement d'une fiche ----------
  const champActif = (u) => {
    const a = document.activeElement ? document.activeElement.id : '';
    return ['sscc', ...champs(u)].find((k) => a === `${k}-${u.id}`) || null;
  };
  const relancerAttente = (sauf) => {
    for (const uid of [...st.attente]) {
      const x = st.units.find((y) => y.id === uid);
      if (x && x !== sauf) enregistrerFiche(x);
    }
  };
  const ecrireFiche = async (u, champ) => {
    const s = statutFiche(u.id);
    if (!st.dto || st.supprimees.has(u.id) || !st.units.includes(u)) return;
    const su = st.dto.units.find((x) => x.id === u.id);
    if (!su) return;
    // Le champ en cours de frappe (autre que celui qu'on valide) part à sa sortie, pas avant ;
    // une valeur déjà refusée et non retouchée ne repart pas.
    const actif = champActif(u);
    const exclure = [...(actif && actif !== champ ? [actif] : []), ...Object.keys(u.refus || {})];
    // Seuls les champs modifiés sur CET appareil partent ; déjà identiques au serveur → propres.
    const { body, differes, egaux } = diffFiche(st, u, su, { exclure, serveurUnits: st.dto.units });
    for (const k of egaux) u.sales.delete(k);
    if (differes.length) st.attente.add(u.id); else st.attente.delete(u.id);
    let reseau = null, ssccPose = false;
    // Un champ par requête : un refus vise exactement la valeur fautive.
    for (const [cle, v] of Object.entries(body)) {
      const k = CHAMP_LOCAL[cle];
      try {
        appliquer(await readyOrders.patchUnit(st.dto.id, u.id, { [cle]: v }));
        champEnregistre(st, u, k, v);   // enregistrée : le champ redevient propre (sauf frappe depuis)
        if (u.refus) delete u.refus[k];
        if (cle === 'sscc') ssccPose = true;
      } catch (e) {
        const d = donnees(e);
        if (!refusServeur(e)) {
          // Réseau, ou commande figée par un envoi : la valeur reste à l'écran, à réessayer.
          if (d.dto) appliquer(d.dto);
          if (refusEnvoiFige(d)) st.message = { err: true, txt: d.message };
          reseau = errMsg(e);
          break;
        }
        if (d.dto) appliquer(d.dto);
        u.refus = { ...(u.refus || {}), [k]: d.message || errMsg(e) };
        // Un SSCC refusé ne reste jamais la référence de la fiche : celle du serveur la redevient
        // (sinon le bon SSCC scanné ensuite ouvrirait « SSCC changé… C'est voulu ? »).
        if (k === 'sscc' && u.ref && u.ref.sscc === v) {
          if (su.sscc) u.ref = { sscc: su.sscc, at: u.ref.at };
          else delete u.ref;
        }
      }
    }
    s.erreur = reseau ? `non enregistrée (${reseau})` : null;
    s.ok = !reseau;
    if (ssccPose) relancerAttente(u);
  };
  const enregistrerFiche = (u, champ) => {
    if (st.enFileFiche.has(u.id)) return;   // une écriture de la fiche attend déjà : elle lira les valeurs du moment
    st.enFileFiche.add(u.id);
    const s = statutFiche(u.id);
    s.n += 1;
    enfiler(async () => {
      st.enFileFiche.delete(u.id);
      try { await ecrireFiche(u, champ); } finally { s.n -= 1; }
    });
  };

  // ---------- Enregistrement de l'en-tête ----------
  const ecrireEntete = async (champ) => {
    if (!st.dto) return;
    const actif = document.activeElement ? document.activeElement.id : '';
    const exclure = ['ord', 'fl', 'fw'].filter((k) => k === actif && k !== champ);
    if (st.refusEntete.ord) exclure.push('ord');
    // Seul ce qui a été changé SUR CET APPAREIL part ; déjà identique au serveur → propre.
    const { body, egaux } = diffEntete(st, st.dto, { exclure });
    for (const k of egaux) st.salesEntete.delete(k);
    const groupes = [];
    if ('orderRef' in body) groupes.push(['ord', { orderRef: body.orderRef }]);
    if ('palletFormat' in body) {
      const { palletFormat, palletLengthCm, palletWidthCm } = body;
      groupes.push(['fmt', palletLengthCm === undefined ? { palletFormat } : { palletFormat, palletLengthCm, palletWidthCm }]);
    }
    let reseau = null;
    for (const [cle, corps] of groupes) {
      try {
        appliquer(await readyOrders.patch(st.dto.id, corps));
        enteteEnregistre(st, corps);   // enregistré : redevient propre (sauf retouché depuis)
        delete st.refusEntete[cle];
        if (cle === 'ord') st.lienExistant = null;
      } catch (e) {
        const d = donnees(e);
        if (d.dto) appliquer(d.dto);
        // La réf. est devenue figée pour cet écran — verrouillée (1er mail parti, 409 VERROUILLE), ou commande
        // de test sans le droit (403 TEST_RESERVE_ADMIN, ou 409 REESSAYER si le passage en test est arrivé entre
        // la lecture et l'écriture — revue 17/10) : la réf. du serveur revient, UN message dit pourquoi
        // (toujours le même pour la commande de test), rien ne reste refusé ni « à réessayer » ; le format part encore.
        const figeeTest = testFige(st.dto) && !st.dto.lockedAt;
        if (cle === 'ord' && (d.error === 'VERROUILLE' || figeeTest)) {
          st.ord = st.dto.orderRef;
          st.salesEntete.delete('ord');   // la réf. suit de nouveau le serveur (changée ensuite par un admin)
          delete st.refusEntete.ord;
          st.lienExistant = null;
          // (commande de test : la note sous le client le dit déjà — pas deux fois le même texte)
          if (!figeeTest) st.message = { err: true, txt: d.message || errMsg(e) };
          continue;
        }
        if (!refusServeur(e)) {
          if (refusEnvoiFige(d)) st.message = { err: true, txt: d.message };
          reseau = errMsg(e);
          break;
        }
        st.refusEntete[cle] = d.message || errMsg(e);
        if (d.error === 'COMMANDE_EXISTE' && d.existingId) st.lienExistant = d.existingId;
      }
    }
    st.sauvEntete.erreur = reseau ? `En-tête non enregistré (${reseau})` : null;
    st.sauvEntete.ok = !reseau;
  };
  const enregistrerEntete = (champ) => {
    if (st.enFileEntete) return;
    st.enFileEntete = true;
    st.sauvEntete.n += 1;
    enfiler(async () => {
      st.enFileEntete = false;
      try { await ecrireEntete(champ); } finally { st.sauvEntete.n -= 1; }
    });
  };

  // ---------- Nombre d'unités ----------
  // « + » / nombre tapé : le serveur crée les fiches vides (PUT counts) ; « − » retire la
  // dernière fiche VIDE de ce type à l'écran, où qu'elle soit ; jamais une fiche remplie.
  const appliquerCompteur = (type, n) => {
    const cur = ofType(st, type).length;
    if (n > cur) {
      st.cible[type] = n;
      return enfiler(async () => {
        try { appliquer(await readyOrders.setCount(st.dto.id, type, n)); return true; } catch (e) {
          const d = donnees(e);
          if (d.dto) appliquer(d.dto);
          st.countMsg = d.message || errMsg(e);
          return false;
        } finally { delete st.cible[type]; }
      });
    }
    const { fiches, assez } = videsARetirer(st, type, cur - n);
    if (!assez) st.countMsg = msgToutesRemplies(type);
    // Une fiche retirée emporte ses états d'enregistrement (sinon l'envoi resterait bloqué) — gardés
    // pour la remettre telle quelle si le serveur refuse.
    const gardes = fiches.map((u) => retirerFiche(st, u));
    st.cible[type] = ofType(st, type).length;
    maj();
    return enfiler(async () => {
      let ok = true;
      try {
        for (const g of gardes) {
          const u = g.u;
          // Jamais de confirmation ici : le serveur ne retire la fiche que si elle est VIDE chez lui.
          try { const r = await readyOrders.deleteUnit(st.dto.id, u.id); st.supprimees.delete(u.id); appliquer(r); } catch (e) {
            if (e?.response?.status === 404) { st.supprimees.delete(u.id); continue; }
            ok = false;
            const d = donnees(e);
            remettreFiche(st, g);
            if (d.dto) appliquer(d.dto);   // remplie ailleurs : elle revient avec les valeurs du serveur
            enregistrerFiche(u);
            st.countMsg = d.error === 'UNITE_REMPLIE' ? msgRempliAilleurs(u.type) : `Fiche vide non retirée : ${errMsg(e)}`;
          }
        }
        if (!ok) appliquer(st.dto);   // la fiche non retirée revient à l'écran
        else relancerAttente();
      } finally { delete st.cible[type]; }
      return ok && assez;
    });
  };
  // null = refus (un message, AUCUNE question ouverte n'est fermée : un appui refusé n'a pas
  // d'effet de bord) ; 'question' = confirmation demandée ; sinon une promesse (true = fait).
  const setCount = (type, wanted, force) => {
    if (st.cible[type] !== undefined) return null;   // changement précédent pas encore enregistré
    const plan = planCompteur(st, type, wanted, force);
    const refus = (msg) => { st.countMsg = msg; maj(); return null; };
    if (plan.erreur) return refus(plan.erreur);
    const { cur, n } = plan;
    if (n === cur) return refus(plan.plafond ? `Maximum ${MAX_UNITS} unités par commande.` : '');
    if (n < cur && !ofType(st, type).some(isEmpty)) return refus(msgToutesRemplies(type));
    touched();
    st.countMsg = plan.msg;
    if (st.countInfoType === type) st.countInfo = '';   // un nouveau nombre du même type répond à la question
    // Gros ajout, OU valeur ramenée au plafond (« 45 » tapé → 10 possibles) : on demande toujours.
    if (plan.demander) {
      st.countAsk = { type, n, plafond: plan.plafond, id: ++askSeq.current };
      focaliser('countAskTxt');
      return 'question';
    }
    return appliquerCompteur(type, n);
  };
  // Après un nombre accepté : le curseur part au premier champ à remplir quand les fiches
  // existent. En attendant la réponse, il quitte le compteur (un scan n'y atterrit pas).
  const apresCompteur = (r, type, attendu) => {
    focaliser('countNote', false);
    r.then((ok) => {
      if (ok && ofType(st, type).length === attendu) focaliser(cibleAllerA(st, st.units));
      else focaliser(type === 'PAL' ? 'npal' : 'ncol');
    });
  };
  const onEnterCompteur = (e, type) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const t = st.cnt[type] ?? String(ofType(st, type).length);
    st.cnt[type] = null;
    const r = setCount(type, t);
    if (st.countAsk) return;
    const v = t.trim();
    if (r === null) {
      // Nombre inchangé : on part aux fiches ; valeur refusée : on reste, sélectionnée.
      if (/^\d+$/.test(v) && ofType(st, type).length === Number(v)) { focaliser(cibleAllerA(st, st.units)); return; }
      st.cnt[type] = t;
      e.target.select();
      maj();
      return;
    }
    if (r && r.then) apresCompteur(r, type, Number(v));
  };
  const onBlurCompteur = (type) => {
    const t = st.cnt[type];
    st.cnt[type] = null;
    if (t !== null && t.trim() !== String(ofType(st, type).length)) setCount(type, t);
    else maj();
  };
  // La question vue au moment de l'appui : si le compteur a changé entre l'appui et le clic (sortie
  // du champ → nouvelle question dans la même boîte), le clic ne fait rien et la nouvelle question
  // reste affichée pour être lue.
  const noterQuestion = () => { askVu.current = st.countAsk ? st.countAsk.id : null; };
  const questionVue = () => {
    const a = st.countAsk, ok = !!a && a.id === askVu.current;
    askVu.current = null;
    if (!ok && a) focaliser('countAskTxt');
    return ok ? a : null;
  };
  const ouiCompteur = (e) => {
    if (e.detail === 0) noterQuestion();
    const a = questionVue();
    if (!a) return;
    const r = setCount(a.type, String(a.n), true);
    if (r && r.then) apresCompteur(r, a.type, a.n);
  };
  const nonCompteur = (e) => {
    if (e.detail === 0) noterQuestion();
    const a = questionVue();
    if (!a) return;
    st.countAsk = null; st.countMsg = '';
    focaliser(a.type === 'PAL' ? 'npal' : 'ncol');
  };
  const plusMoins = (type, delta) => {
    if (st.cible[type] !== undefined) return;
    const r = setCount(type, Math.max(0, ofType(st, type).length + delta));
    if (r === null) maj();
  };
  // « ＋ Palette / ＋ Colis » : une fiche vide après la dernière du même type, focus sur son SSCC.
  const addAndFocus = (type) => {
    if (st.units.length >= MAX_UNITS || st.ajoutEnCours) return;   // un clic refusé ne ferme aucune question
    touched();
    if (st.countInfoType === type) st.countInfo = '';
    st.ajoutEnCours = true;
    focaliser('progTxt', false);   // pas d'Entrée de scan sur « ＋ » pendant l'attente
    enfiler(async () => {
      try {
        const r = await readyOrders.addUnit(st.dto.id, type);
        appliquer(r);
        if (r && r.newUnitId) focaliser('sscc-' + r.newUnitId, 'center');
      } catch (e) {
        const d = donnees(e);
        if (d.dto) appliquer(d.dto);
        st.countMsg = d.message || errMsg(e);
      } finally { st.ajoutEnCours = false; }
    });
  };

  // ---------- Suppression d'une fiche : confirmation séparée, désarmée seule ----------
  // Une seule question ouverte à la fois : armer une suppression ferme la confirmation d'envoi
  // et la question « Passer de X à Y ? ». Le focus va sur « Annuler », jamais sur « Supprimer » :
  // l'Entrée d'un scan ne peut pas valider la suppression.
  const arm = (u) => {
    disarm();
    st.armed = u.id;
    if (st.phase === 'confirm') st.phase = 'edit';
    fermerQuestionCompteur();
    st.supprQuestion = false;
    st.armTimer = setTimeout(() => { if (st.armed === u.id) { disarm(); maj(); } }, 5000);
    focaliser('del-no-' + u.id);
  };
  // Après une suppression, le focus va sur le prochain champ à remplir (jamais sur un bouton).
  // `confirmee` : « Supprimer Palette N » confirmé. Le serveur ne supprime une fiche REMPLIE
  // (chez lui) que sur cette confirmation, et seulement si CET écran la montre remplie
  // (optionsSuppression) : ✕ sur une fiche qui paraît vide → refus si un autre appareil l'a remplie.
  const retirer = (u, confirmee = false) => {
    const i = st.units.indexOf(u);
    if (i < 0) return;
    const nom = frName(st, u);
    const options = optionsSuppression(u, confirmee);
    touched();
    // Quitte l'écran avec ses états (enregistrement, photos à renvoyer, SSCC en attente), gardés
    // pour la remettre TELLE QU'ELLE si le serveur refuse la suppression.
    const garde = retirerFiche(st, u);
    focaliser(cibleAllerA(st, tourDepuis(i)));
    enfiler(async () => {
      try {
        const r = await readyOrders.deleteUnit(st.dto.id, u.id, options);
        st.supprimees.delete(u.id);
        appliquer(r);
        relancerAttente();
      } catch (e) {
        if (e?.response?.status === 404) { st.supprimees.delete(u.id); return; }   // déjà supprimée
        const d = donnees(e);
        remettreFiche(st, garde);   // revient avec ses frappes et ses photos à renvoyer…
        appliquer(d.dto || st.dto);   // … et les valeurs du serveur pour ses champs non touchés ici
        enregistrerFiche(u);          // frappes restées en route pendant la suppression : renvoyées
        st.message = { err: true, txt: d.error === 'UNITE_REMPLIE' ? msgRempliAilleurs(u.type) : `${nom} n'a pas été supprimée : ${errMsg(e)}` };
      }
    });
  };

  // ---------- Photos ----------
  // Après une photo, le curseur repart au prochain champ à faire (cette fiche d'abord) : le scan
  // de l'étiquette suivante n'atterrit jamais sur un bouton photo. L'écran ne défile pas.
  const apresPhoto = (u) => {
    const k = st.units.indexOf(u);
    focaliser(cibleAllerA(st, k >= 0 ? tourDepuis(k) : st.units), false);
  };
  const envoyerPhoto = async (u, f, s) => {
    try {
      if (!st.dto || st.supprimees.has(u.id) || !st.units.includes(u)) {
        u.photosEnCours -= 1;
        // Fiche en route vers la suppression : la photo est gardée sur la fiche — si le serveur
        // refuse la suppression, elle rejoint les photos à renvoyer (remettreFiche).
        if (st.supprimees.has(u.id)) (u.photosSuspendues || (u.photosSuspendues = [])).push(f);
        return;
      }
      // TOUJOURS ré-encodée (JPEG 85 %, côté long 2000 px) : les métadonnées (GPS, appareil) ne
      // partent jamais chez le client. Illisible → refusée (PHOTO_ILLISIBLE), jamais l'original.
      const envoi = await reduirePhoto(f);
      const r = await readyOrders.uploadPhoto(st.dto.id, u.id, envoi);
      u.photosEnCours -= 1;
      appliquer(r);
    } catch (e) {
      u.photosEnCours -= 1;
      const d = donnees(e);
      if (e?.code === 'PHOTO_ILLISIBLE') {
        st.message = { err: true, txt: `${frName(st, u)} : photo refusée — ${e.message}` };
      } else if (refusServeur(e)) {
        if (d.dto) appliquer(d.dto);
        st.message = { err: true, txt: `${frName(st, u)} : photo refusée — ${d.message || errMsg(e)}` };
      } else {
        // Réseau, ou commande figée par un envoi : la photo est gardée pour « réessayer ».
        if (d.dto) appliquer(d.dto);
        if (refusEnvoiFige(d)) st.message = { err: true, txt: d.message };
        const l = st.photosEchec.get(u.id) || [];
        l.push(f);
        st.photosEchec.set(u.id, l);
        s.erreur = `photo non envoyée (${errMsg(e)})`;
      }
      appliquer(st.dto);   // recompte les photos de la fiche (et oublie celles d'une fiche supprimée)
    } finally { s.n -= 1; }
  };
  const ajouterPhotos = (u, liste) => {
    const libres = MAX_PHOTOS - (u.photos || 0);
    const files = Array.from(liste || []).slice(0, Math.max(0, libres));
    if (!files.length) return;
    touched();
    u.photosEnCours = (u.photosEnCours || 0) + files.length;
    u.photos = (u.photos || 0) + files.length;
    const s = statutFiche(u.id);
    s.n += files.length;
    apresPhoto(u);
    for (const f of files) enfiler(() => envoyerPhoto(u, f, s));
  };
  const retirerPhoto = (u) => {
    const pid = u.photoIds && u.photoIds[u.photoIds.length - 1];
    if (!pid) return;
    touched();
    st.photosMasquees.add(pid);
    appliquer(st.dto);
    apresPhoto(u);
    const s = statutFiche(u.id);
    s.n += 1;
    enfiler(async () => {
      try {
        const r = await readyOrders.deletePhoto(st.dto.id, u.id, pid);
        st.photosMasquees.delete(pid);
        appliquer(r);
      } catch (e) {
        st.photosMasquees.delete(pid);
        if (e?.response?.status !== 404) st.message = { err: true, txt: `${frName(st, u)} : photo non retirée — ${errMsg(e)}` };
        appliquer(st.dto);
      } finally { s.n -= 1; }
    });
  };
  // « non enregistrée — touchez pour réessayer » : valeurs ET photos restées en route.
  const reessayer = (u) => {
    const s = statutFiche(u.id);
    s.erreur = null;
    const l = st.photosEchec.get(u.id) || [];
    st.photosEchec.delete(u.id);
    if (l.length) {
      u.photosEnCours = (u.photosEnCours || 0) + l.length;
      u.photos = (u.photos || 0) + l.length;
      s.n += l.length;
      for (const f of l) enfiler(() => envoyerPhoto(u, f, s));
    }
    enregistrerFiche(u);
  };
  // Seulement les fiches PRÉSENTES (une fiche supprimée a été oubliée : oublierFiche).
  const reessayerTout = () => {
    for (const u of st.units) {
      const s = st.sauv.get(u.id);
      if ((s && s.erreur) || st.photosEchec.has(u.id)) reessayer(u);
    }
    if (st.sauvEntete.erreur) { st.sauvEntete.erreur = null; enregistrerEntete(); }
  };

  // ---------- Saisie dans une fiche ----------
  const onEnterSscc = (e, u) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    valider(st, u);
    // Valeur fausse (illisible, en double) OU vide : on reste dessus, sélectionnée.
    if (entreeFausse(st, u, 'sscc')) { e.target.select(); maj(); return; }
    enregistrerFiche(u, 'sscc');
    focaliser(cibleAvancer(st, u, 'sscc-' + u.id).id);
  };
  // Une Entrée de trop (double appui, lecteur en CR+LF) ne saute jamais un champ — sinon
  // hauteur et poids s'inverseraient sans alerte.
  const onEnterMesure = (e, u, k) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (entreeFausse(st, u, k)) { e.target.select(); maj(); return; }
    enregistrerFiche(u, k);
    focaliser(cibleAvancer(st, u, `${k}-${u.id}`).id);
  };
  const garderNouveau = (u) => {
    if (!enAttente(st, u)) return;
    u.ref = { sscc: normSscc(u.sscc), at: u.ssccAt };
    touched();
    enregistrerFiche(u, 'sscc');
    focaliser(cibleAvancer(st, u, 'sscc-' + u.id).id);
  };
  const remettre = (u) => {
    const r = u.ref;
    if (!r) return;
    touched();
    u.sscc = r.sscc; u.ssccAt = r.at;
    salir(u, 'sscc');
    if (u.refus) delete u.refus.sscc;
    enregistrerFiche(u, 'sscc');
    focaliser(cibleAvancer(st, u, 'sscc-' + u.id).id);
  };

  // ---------- En-tête : réf. de chargement (ODP, seul identifiant), format ----------
  // (a) Entrée/scan valide la réf. et passe au compteur de palettes s'il n'y a aucune fiche,
  // sinon au premier champ À FAIRE : le scan SSCC suivant ne s'ajoute jamais à la référence.
  const suiteEntete = () => (!st.units.length ? 'npal' : cibleAllerA(st, st.units));
  const onEnterOrd = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    st.vuEntete.ord = true;
    if (verifRefChargement(st.ord) || st.refusEntete.ord) { e.target.select(); maj(); return; }
    st.ord = normRefChargement(st.ord);
    enregistrerEntete('ord');
    focaliser(suiteEntete());
  };
  const onBlurEntete = (k) => {
    st.vuEntete[k] = true;
    if (k === 'ord' && !verifRefChargement(st.ord)) st.ord = normRefChargement(st.ord);
    enregistrerEntete(k);
    maj();
  };
  const onEnterFormat = (e, k) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (k === 'fl') { focaliser('fw'); return; }
    if (!formatEtat(st).ok) { e.target.select(); return; }
    enregistrerEntete('fw');
    focaliser(suiteEntete());
  };

  // ---------- v2 : client et commande de test (tout en haut) ----------
  // Enregistrés aussitôt (PATCH clientId / isTest), dans la file des écritures ; refusés par le
  // serveur après le 1er mail (409 VERROUILLE), ou si ce client a déjà sa commande pour cette réf.
  // (409 COMMANDE_EXISTE → lien vers elle). Un refus (ou le réseau) laisse le client du serveur.
  // Après le choix : la réf. si elle manque, sinon la suite — jamais la réf. déjà posée (un scan
  // d'étiquette la remplacerait).
  const apresClient = () => (!st.dto.lockedAt && (verifRefChargement(st.ord) || st.refusEntete.ord) ? 'ord' : suiteEntete());
  const changerClient = (c) => {
    if (!c || !st.dto) return;
    const actuel = st.clientEnCours || st.dto.client;
    if (actuel && actuel.id === c.id) { st.refusClient = null; st.lienExistantClient = null; focaliser(apresClient()); return; }
    touched();
    st.clientEnCours = c; st.refusClient = null; st.lienExistantClient = null;
    const seq = ++st.clientSeq;
    focaliser(apresClient());
    enfiler(async () => {
      try {
        appliquer(await readyOrders.patch(st.dto.id, { clientId: c.id }));
        if (st.clientSeq === seq) { st.refusClient = null; st.lienExistantClient = null; }
        // « La réf. X a déjà sa commande » valait pour l'ANCIEN client : une fois le client CHANGÉ (pas
        // avant : refusé, il reste valable), la réf. tapée repart pour le nouveau client.
        if (st.refusEntete.ord) {
          delete st.refusEntete.ord;
          st.lienExistant = null;
          if (enteteSale(st, 'ord')) enregistrerEntete('ord');
        }
      } catch (e) {
        const d = donnees(e);
        if (d.dto) appliquer(d.dto);
        // Seul le refus du DERNIER choix s'affiche (un choix plus récent l'a remplacé). Devenue une commande de
        // test sans le droit (403, ou 409 REESSAYER — revue 17/10) : le champ est figé, sa note dit pourquoi —
        // aucun refus « réessayez » à côté d'un champ inutilisable.
        if (st.clientSeq === seq && testFige(st.dto) && !st.dto.lockedAt) {
          st.refusClient = null; st.lienExistantClient = null;
        } else if (st.clientSeq === seq) {
          st.refusClient = e?.response ? (d.message || errMsg(e)) : `Client non enregistré (${errMsg(e)}) : choisissez-le à nouveau.`;
          if (d.error === 'COMMANDE_EXISTE' && d.existingId) st.lienExistantClient = d.existingId;
        }
      } finally { if (st.clientSeq === seq) st.clientEnCours = null; }
    });
  };
  const changerTest = (v) => {
    if (!st.dto) return;
    touched();
    st.testEnCours = v; st.refusTest = null;
    const seq = ++st.testSeq;   // seule la DERNIÈRE écriture rend la case au serveur (pas de clignotement)
    enfiler(async () => {
      try {
        appliquer(await readyOrders.patch(st.dto.id, { isTest: v }));
        if (st.testSeq === seq) st.refusTest = null;
      } catch (e) {
        const d = donnees(e);
        if (d.dto) appliquer(d.dto);
        if (st.testSeq === seq) st.refusTest = e?.response ? (d.message || errMsg(e)) : `Non enregistré (${errMsg(e)}) : touchez la case à nouveau.`;
      } finally { if (st.testSeq === seq) st.testEnCours = null; }
    });
  };

  // ---------- Envoi ----------
  const etatEnvoi = () => {
    const dto = st.dto;
    const envoyes = (dto.mails || []).filter((m) => m.state === 'ENVOYE');
    const dernier = envoyes[envoyes.length - 1];
    const sent = !!dto.lastSentHash;
    const dirty = sent && dto.hash !== dto.lastSentHash;
    const histo = envoyes.map((m) => `${m.kind === 'UPDATE' ? 'Correction' : 'Mail'} ${heure(m.sentAt)}`).join(' · ');
    const label = sent ? '✉ Envoyer la correction' : '✅ Commande prête — envoyer le mail';
    const b = blockers(st);
    const question = st.countAsk ? 'le nombre d\'unités' : st.armed ? 'la suppression d\'une fiche' : '';
    const erreurSauv = saisiesNonEnregistrees(st);   // fiches présentes seulement + en-tête
    // `cible` (action « aller ») : où le bouton mène, calculée ICI une fois — clicPret / ouvrirRecap la reprennent
    // (13e revue) ; la réf. SAUTÉE quand elle est figée pour cette personne (commande de test, sans le droit).
    const versBloqueur = () => cibleBloqueur(st, { sansRef: testFige(dto) });
    const res = { label, sent, dirty, histo, dernier, disabled: false, aria: false, note: '', err: false, action: null, cible: null };
    if (st.envoi.etat === 'envoi' || dto.status === 'ENVOI_EN_COURS') {
      return { ...res, label: '⏳ Envoi en cours…', disabled: true, note: st.envoi.etat === 'envoi' ? 'Envoi du mail en cours, ne quittez pas la page.' : 'Un envoi est en cours (autre appareil ?) : rechargez dans un instant.' };
    }
    if (dto.status === 'A_VERIFIER') {
      return { ...res, label: '⚠ Envoi à vérifier', disabled: true, err: true, note: admin ? 'Le mail est peut-être parti : vérifiez les Éléments envoyés de zeus@, puis choisissez ci-dessous.' : 'Le mail est peut-être parti : un admin doit vérifier les Éléments envoyés de zeus@.' };
    }
    // Une saisie restée en route (réseau) : l'écran ne montre plus ce que le serveur a, même
    // après un envoi — on le dit avant tout le reste, et le bouton relance l'enregistrement.
    if (erreurSauv) return { ...res, aria: true, err: true, action: 'reessayer', note: 'Des saisies ne sont pas enregistrées (réseau) : touchez le bouton (ou la fiche) pour réessayer.' };
    if (st.attente.size) return { ...res, aria: true, err: true, action: 'relancer', note: 'Un SSCC attend d\'être enregistré (il est encore sur une autre fiche côté serveur) : corrigez l\'autre fiche, ou touchez le bouton pour réessayer.' };
    if (sent && !dirty && !b.length) {
      return { ...res, label: `✔ ${dernier && dernier.kind === 'UPDATE' ? 'Correction envoyée' : 'Mail envoyé'} à ${dernier ? heure(dernier.sentAt) : '—'}`, disabled: true, note: `Envois : ${histo}. Si une valeur change, Zeus propose d'envoyer une correction.` };
    }
    if (question) return { ...res, disabled: true, err: true, note: `Répondez d'abord à la question sur ${question} (au-dessus).` };
    if (st.phase === 'confirm') return { ...res, disabled: true, note: '' };
    if (!st.units.length) return { ...res, disabled: true, note: 'Ajoutez au moins une unité.' };
    // (b) Tant qu'il reste quelque chose à faire, le bouton MÈNE à ce qui manque (champ à
    // saisir, ou fiche à laquelle il ne manque que la photo) au lieu d'ouvrir l'envoi.
    // v2 : une commande sans client (champ vide côté serveur) ne part pas — le bouton mène au champ.
    const clientManque = 'client' in dto && !dto.client && !st.clientEnCours && !dto.lockedAt;
    // (revue 17/10) Commande de test, sans le droit : client et réf. figés (champs désactivés). Client manquant,
    // réf. à corriger (ancienne réf. invalide) : la note dit qui peut les changer — une fois chacun, jamais repris
    // dans « Encore à compléter » (11e revue) — et le bouton mène encore à ce qui reste (10e revue), jamais à un
    // champ figé : (12e revue) la réf. figée est SAUTÉE (cibleBloqueur sansRef) — fiches à compléter derrière une
    // réf. bloquante : le bouton y mène ; `autres` = `b` sans la réf. (pas un 2e calcul de blockers).
    const refBloque = bloqueursRef(st).length > 0;
    if (testFige(dto) && !dto.lockedAt && (clientManque || refBloque)) {
      const autres = sansBloqueursRef(st, b);
      const cible = autres.length ? versBloqueur() : ID_NOTE;
      const reste = cible !== ID_NOTE;
      const note = noteEssaiFige({ clientManque, refBloque, autres });
      return { ...res, aria: reste, err: true, disabled: !reste, action: reste ? 'aller' : null, cible: reste ? cible : null, note };
    }
    if (clientManque) return { ...res, aria: true, err: true, action: 'client', note: 'Choisissez le client de cette commande (tout en haut).' };
    if (b.length) return { ...res, aria: true, err: true, action: 'aller', cible: versBloqueur(), note: texteACompleter(b) };
    if (enFile.current > 0 || st.attenteRecap) return { ...res, action: 'ouvrir', note: '⏳ Enregistrement en cours…' };
    const bServeur = dto.bloqueurs || [];
    // (revue 17/10) Commande de test, sans le droit : ce qui bloque vient du client (inactif, non proposé, sans
    // destinataire…) — il ne peut pas en changer, la note dit qui peut (pas de bouton qui mène ailleurs).
    // (9e revue : aussi après le 1er mail — un client désactivé ne dépend pas du verrou.) (11e revue) Client
    // manquant ou supprimé : le message de #clientManque, jamais le « choisissez le client » du serveur.
    if (testFige(dto) && (dto.bloqueursClient || []).length) {
      if ('client' in dto && !dto.client) return { ...res, disabled: true, err: true, note: noteEssaiFige({ clientManque: true, verrouille: !!dto.lockedAt }) };
      return { ...res, disabled: true, err: true, note: `${dto.bloqueursClient.slice(0, 2).join(' · ')} — ${dto.lockedAt ? MSG_CLIENT_TEST_ADMIN_VERROU : MSG_CLIENT_TEST_ADMIN}` };
    }
    if (bServeur.length) return { ...res, aria: true, err: true, action: 'aller', cible: versBloqueur(), note: listeCourte('Le serveur signale', bServeur) };
    return { ...res, action: 'ouvrir', note: sent ? `Envois : ${histo}. ${dto.isTest ? 'La correction partira à l\'adresse de test.' : `La correction remplacera les valeurs chez ${(dto.client && dto.client.name) || 'le client'}.`}` : 'Tout est complet : le mail peut partir.' };
  };
  // Le focus va sur le récapitulatif, jamais sur « Confirmer l'envoi » : une Entrée de scanner
  // ou un appui répété ne peut pas envoyer le mail sans relecture.
  const ouvrirRecap = async () => {
    if (st.attenteRecap) return;   // déjà en attente des derniers enregistrements
    if (enFile.current > 0) {
      st.attenteRecap = true; maj();
      while (enFile.current > 0) await file.current;   // les dernières saisies d'abord
      st.attenteRecap = false;
    }
    const e = etatEnvoi();
    if (e.action !== 'ouvrir') { if (e.action === 'aller') focaliser(e.cible); else if (e.action === 'client') focaliser('cli'); else maj(); return; }
    disarm();
    st.countAsk = null;
    st.supprQuestion = false;
    st.phase = 'confirm';
    const seq = st.envoi.seq + 1;
    st.envoi = { etat: 'apercu', apercu: null, erreur: null, seq };
    focaliser('sendRecap');
    try {
      const a = await readyOrders.previewMail(st.dto.id);
      if (st.phase === 'confirm' && st.envoi.seq === seq) {
        // Le serveur a changé depuis ce que l'écran montre (autre appareil) : pas d'envoi sur cet
        // aperçu — la commande est rechargée, l'opérateur relit avant de confirmer.
        if (a.hash && a.hash !== st.dto.hash) {
          st.envoi.erreur = 'La commande a changé sur un autre appareil : elle est rechargée, relisez-la avant d\'envoyer.';
          rafraichir();
        } else st.envoi.apercu = a;
      }
    } catch (err) {
      if (st.phase === 'confirm' && st.envoi.seq === seq) st.envoi.erreur = errMsg(err);
    }
    maj();
  };
  const clicPret = () => {
    const e = etatEnvoi();
    if (e.disabled) return;
    if (e.action === 'aller') { focaliser(e.cible); return; }
    if (e.action === 'client') { focaliser('cli'); return; }
    if (e.action === 'reessayer') { reessayerTout(); return; }
    if (e.action === 'relancer') { relancerAttente(); return; }
    if (e.action === 'ouvrir') ouvrirRecap();
  };
  const annulerEnvoi = () => { st.phase = 'edit'; st.envoi = { ...st.envoi, etat: 'repos', apercu: null, erreur: null }; focaliser(ID_NOTE); };
  const rafraichir = () => enfiler(async () => {
    try { appliquer(await readyOrders.get(st.dto.id)); } catch (e) { st.message = { err: true, txt: `Rechargement impossible : ${errMsg(e)}` }; }
  });
  const confirmer = async () => {
    // Un 2e appui rapide ne renvoie rien : la confirmation est déjà en cours d'envoi.
    if (st.phase !== 'confirm' || st.envoi.etat !== 'apercu' || !st.envoi.apercu || st.armed || st.countAsk || enFile.current > 0) return;
    // L'empreinte de ce que l'écran MONTRE (dernier état du serveur reçu) : si le serveur a changé
    // depuis, il refuse (409 CHANGEE, dto joint) au lieu d'envoyer des valeurs que personne n'a relues.
    const expectedHash = st.dto.hash;
    st.envoi = { ...st.envoi, etat: 'envoi' };
    maj();
    let sansReponse = false;
    try {
      const r = await readyOrders.send(st.dto.id, expectedHash);
      appliquer(r);
      st.avertEnvoi = r.avertissements || [];
      const m = r.mail || {};
      st.message = { ok: true, txt: `✔ ${m.kind === 'UPDATE' ? 'Correction envoyée' : 'Mail envoyé'}${m.to && m.to.length ? ` à ${m.to.join(', ')}` : ''}.` };
    } catch (e) {
      const d = donnees(e);
      if (d.dto) appliquer(d.dto);
      if (Array.isArray(d.avertissements)) st.avertEnvoi = d.avertissements;
      if (!e.response) {
        sansReponse = true;
        st.message = { err: true, txt: 'Pas de réponse du serveur : le mail est peut-être parti. La commande est rechargée ; vérifiez son état avant tout nouvel envoi.' };
      } else {
        st.message = { err: true, txt: d.message || errMsg(e), liste: Array.isArray(d.bloqueurs) ? d.bloqueurs : null };
      }
    }
    st.phase = 'edit';
    st.envoi = { ...st.envoi, etat: 'repos', apercu: null, erreur: null };
    focaliser(ID_NOTE);
    if (sansReponse) rafraichir();
  };
  const resoudre = async (m, parti) => {
    const q = parti
      ? 'Le mail est bien dans les Éléments envoyés de zeus@ ? Il sera marqué « envoyé » (pas de renvoi).'
      : 'Le mail n\'est PAS dans les Éléments envoyés de zeus@ ? La commande pourra être renvoyée avec « Commande prête ».';
    if (!window.confirm(q)) return;
    try {
      appliquer(await readyOrders.resolve(st.dto.id, m.id, parti));
      st.message = { ok: true, txt: parti ? 'Envoi marqué comme parti.' : 'Envoi marqué comme non parti : vous pouvez renvoyer la commande.' };
    } catch (e) {
      const d = donnees(e);
      if (d.dto) appliquer(d.dto);
      st.message = { err: true, txt: d.message || errMsg(e) };
    }
    maj();
  };

  // ---------- « Supprimer le brouillon » (16-10-2026) ----------
  // Question SUR LA PAGE (jamais window.confirm), focus sur « Annuler » : une Entrée de scanner ne
  // supprime rien. La suppression passe APRÈS les écritures en attente (même file). Supprimée → la liste.
  const demanderSuppressionCommande = () => {
    disarm();
    fermerQuestionCompteur();
    if (st.phase === 'confirm') { st.phase = 'edit'; st.envoi = { ...st.envoi, etat: 'repos', apercu: null, erreur: null }; }
    st.message = null;
    st.supprQuestion = true;
    focaliser('supprNon');
  };
  const annulerSuppressionCommande = () => { st.supprQuestion = false; focaliser(ID_NOTE, false); };
  const supprimerCommande = () => {
    if (!st.supprQuestion || st.supprEnCours) return;
    st.supprEnCours = true;
    maj();
    enfiler(async () => {
      const cmd = st.dto;
      try {
        await readyOrders.remove(cmd.id);
        navigate('/commande-prete', { replace: true, state: { supprimee: texteSupprimee(cmd) } });
      } catch (e) {
        const d = donnees(e);
        if (d.dto) appliquer(d.dto);
        st.supprQuestion = false;   // (ses boutons disparaissent : le focus tombé, le garde du focus le rattrape)
        st.supprEnCours = false;
        st.message = { err: true, txt: texteRefusSuppression(e, errMsg(e)) };
        if (!e?.response) rafraichir();   // sans réponse : l'état réel est relu
      }
    });
  };

  // =====================================================================
  // Rendu
  // =====================================================================
  if (!autorise) {
    return <div className="cp"><style>{CSS}</style><p className="note err">{texteAccesRefuse(DROIT_LIRE)}</p></div>;
  }
  if (chargement.etat === 'charge') return <div className="cp"><style>{CSS}</style><p className="note">Chargement…</p></div>;
  if (chargement.etat === 'erreur') {
    return (
      <div className="cp">
        <style>{CSS}</style>
        <Breadcrumbs items={[{ label: 'Commandes prêtes', onClick: () => navigate('/commande-prete') }, { label: 'Erreur' }]} />
        <p className="note err">{chargement.introuvable ? 'Commande introuvable.' : `Chargement impossible : ${chargement.msg}`}</p>
        <Link to="/commande-prete" className="btn">← Liste des commandes</Link>
      </div>
    );
  }

  // Une fiche validée dont le SSCC était un doublon reçoit sa référence dès que le doublon est
  // réglé sur l'AUTRE fiche (corrigée ou supprimée).
  for (const u of st.units) if (u.valide) poserRef(st, u);

  const dto = st.dto;
  const locked = !!dto.lockedAt;
  const clientAffiche = st.clientEnCours || dto.client || null;   // v2 : choix en cours d'enregistrement, sinon le serveur
  // Rappel des règles : langues du mail = celles de la liste des clients (le DTO de la commande ne les a pas).
  const infoClient = (clientAffiche && clients && clients.find((c) => c.id === clientAffiche.id)) || clientAffiche;
  const testAffiche = st.testEnCours ?? !!dto.isTest;
  // Unités déjà chargées dans un camion : le serveur refuse un autre client et le passage en test (409).
  const dejaChargee = (dto.units || []).some((u) => u.loadedAt);
  // Commande de test, sans le droit : réf. et client figés (le serveur refuse : 403 TEST_RESERVE_ADMIN).
  const enteteTestFigee = testFige(dto);
  const nPal = ofType(st, 'PAL').length;
  const nCol = ofType(st, 'COL').length;
  const plein = st.units.length >= MAX_UNITS;
  const f = formatEtat(st);
  const done = st.units.filter((u) => complete(st, u)).length;
  const nph = st.units.reduce((t, u) => t + (u.photos || 0), 0);
  const e = etatEnvoi();
  const envoiEnCours = st.envoi.etat === 'envoi';
  const ordMsg = st.refusEntete.ord || ((st.vuEntete.ord || st.ord.trim()) ? verifRefChargement(st.ord) : null);
  // Avertissements déjà montrés à leur place (sous la réf., sous le SSCC de la fiche) : pas en double.
  const avertServeur = (dto.avertissements || []).filter((a) => a !== MSG_REF_INHABITUELLE
    && !a.endsWith(MSG_ETIQ_PAL_SUR_COLIS) && !a.endsWith(MSG_ETIQ_COLIS_SUR_PAL));
  const compteurAffiche = (type) => st.cnt[type] ?? String(st.cible[type] ?? ofType(st, type).length);
  const countNote = [st.countMsg, st.countInfo].filter(Boolean).join(' ');

  const champMesure = (u, k, s) => (
    <div className="field" key={k}>
      <label htmlFor={`${k}-${u.id}`}>{LIBELLES[k]}</label>
      <input id={`${k}-${u.id}`} type="text" inputMode="decimal" autoComplete="off" value={u[k] ?? ''}
        className={s.badKeys && s.badKeys.includes(k) ? 'bad' : ''}
        onFocus={selectOnFocus} onMouseDown={keepSelection} onKeyDown={(ev) => onEnterMesure(ev, u, k)}
        onChange={(ev) => { u[k] = ev.target.value; salir(u, k); if (u.refus) delete u.refus[k]; touched(); maj(); }}
        onBlur={() => enregistrerFiche(u, k)} />
    </div>
  );

  const fiche = (u) => {
    const s = stateOf(st, u);
    const nom = frName(st, u);
    const att = enAttente(st, u);
    const sv = st.sauv.get(u.id);
    const echec = (sv && sv.erreur) || (st.photosEchec.has(u.id) ? 'photo non envoyée' : null);
    const seulePhoto = manquePhoto(st, u);
    const photos = u.photos || 0;
    const avEtiquette = avertFiche(st, u);
    // v2 : unité déjà chargée dans un camion — son étiquette et sa suppression sont figées (le serveur
    // refuse, 409 UNITE_CHARGEE) : contrôles grisés + explication, les mesures restent corrigeables.
    const chargeeLe = u.loadedAt || null;   // (fusionner : loadedAt du serveur)
    return (
      <div className={`card ${s.cls}${echec ? ' nonsauv' : ''}`} id={`card-${u.id}`} key={u.id}>
        <div className="card-head">
          <b>{nom}</b>
          <span className={`state ${s.cls}`}>{s.txt}</span>
        </div>
        <div className="field">
          <label htmlFor={`sscc-${u.id}`}>SSCC</label>
          <div className="scanrow">
            {/* Clavier TEXTE : une étiquette EffiTrace (SKC.HG.PAL.1970) se tape aussi à la main. */}
            <input id={`sscc-${u.id}`} type="text" inputMode="text" autoCapitalize="characters" autoComplete="off" spellCheck={false}
              placeholder="Scanner l'étiquette (SSCC ou EffiTrace)" disabled={!!chargeeLe}
              value={u.sscc} className={s.ssccBad ? 'bad mono' : 'mono'}
              onFocus={selectOnFocus} onMouseDown={keepSelection} onKeyDown={(ev) => onEnterSscc(ev, u)}
              onChange={(ev) => { u.valide = false; setSscc(st, u, ev.target.value); salir(u, 'sscc'); if (u.refus) delete u.refus.sscc; touched(); maj(); }}
              onBlur={() => { valider(st, u); enregistrerFiche(u, 'sscc'); maj(); }} />
            {/* Le lecteur du PDA « tape » dans le champ qui a le focus : Scan l'y place. */}
            <button className="btn small" type="button" id={`scan-${u.id}`} aria-label={`Scanner le SSCC de ${nom}`} disabled={!!chargeeLe}
              onClick={() => focaliser('sscc-' + u.id)}>Scan</button>
          </div>
          {chargeeLe && <span className="note" id={`charge-${u.id}`}>{`🚚 Chargée dans un camion le ${dateHeure(chargeeLe)} : étiquette et suppression figées.`}</span>}
          {avEtiquette && <span className="note warn" id={`etiq-${u.id}`}>⚠ {avEtiquette}</span>}
        </div>
        {/* La question n'apparaît que si la nouvelle valeur est un SSCC valide et unique : on ne
            peut jamais « garder » un code illisible. Le focus va sur le TEXTE de la question. */}
        {att && (
          <div className="confirm warn" role="status">
            <span id={`rempl-txt-${u.id}`} tabIndex={-1}>{`SSCC changé : ${abregeSscc(u.ref.sscc)} → ${abregeSscc(normSscc(u.sscc))}. C'est voulu ?`}</span>
            <button className="btn small" type="button" id={`rempl-ok-${u.id}`} onClick={() => garderNouveau(u)}>{`Oui, garder ${abregeSscc(normSscc(u.sscc))}`}</button>
            <button className="btn small" type="button" id={`rempl-no-${u.id}`} onClick={() => remettre(u)}>{`Non, remettre ${abregeSscc(u.ref.sscc)}`}</button>
          </div>
        )}
        <div className={u.type === 'PAL' ? 'vals' : 'vals col'}>
          {champs(u).map((k) => champMesure(u, k, s))}
        </div>
        {seulePhoto && (
          <span className="note photo-msg" id={idPhoto(u)} tabIndex={-1}>
            📷 Il ne manque que la photo de {nom} : touchez « 📷 Photo » (au moins une par unité).
          </span>
        )}
        <div className="card-foot">
          <span className="fixed">
            {u.type === 'PAL'
              ? (f.ok ? `Longueur × largeur : ${f.l} × ${f.w} cm (format)` : 'Longueur × largeur : à définir dans le format')
              : 'Colis : longueur, largeur, hauteur mesurées'}
          </span>
          <div className="photos">
            {(u.photoIds || []).map((pid, i) => (
              <a key={pid} href={readyOrders.photoUrl(dto.id, u.id, pid)} target="_blank" rel="noreferrer" className="vignette" tabIndex={-1}
                title={`Photo ${i + 1} de ${nom}`}>
                <img src={readyOrders.photoUrl(dto.id, u.id, pid)} alt={`Photo ${i + 1} de ${nom}`} loading="lazy" />
              </a>
            ))}
            {Array.from({ length: u.photosEnCours || 0 }, (_, i) => <span key={'p' + i} className="vignette attente" title="Envoi de la photo…">⏳</span>)}
            <button className="btn small" type="button" id={`phm-${u.id}`} aria-label={`Retirer une photo de ${nom}`}
              disabled={!(u.photoIds && u.photoIds.length)} onKeyDown={sansEntree} onClick={() => retirerPhoto(u)}>−</button>
            <button className={`btn small${seulePhoto ? ' manque' : ''}`} type="button" id={`php-${u.id}`}
              aria-label={photos >= MAX_PHOTOS ? `${nom} : ${photos} photos, maximum atteint` : `Ajouter une photo à ${nom} (${photos})`}
              disabled={photos >= MAX_PHOTOS} onKeyDown={sansEntree}
              onClick={() => { const inp = document.getElementById('phf-' + u.id); if (inp) inp.click(); }}>
              {photos >= MAX_PHOTOS ? `📷 ${photos}/${MAX_PHOTOS} (max)` : `📷 Photo (${photos})`}
            </button>
            <input type="file" accept="image/*" capture="environment" multiple hidden id={`phf-${u.id}`} tabIndex={-1}
              onChange={(ev) => { const l = ev.target.files; ajouterPhotos(u, l); ev.target.value = ''; }} />
            <button className="btn small" type="button" id={`del-${u.id}`} aria-label={`Supprimer ${nom}`} disabled={!!chargeeLe}
              onClick={() => { if (isEmpty(u)) retirer(u, false); else arm(u); }}>✕</button>
          </div>
        </div>
        {st.armed === u.id && (
          <div className="confirm" role="alert">
            <span>{`Supprimer ${nom} et ses données ?`}</span>
            <button className="btn small danger" type="button" id={`del-ok-${u.id}`}
              onClick={() => { if (st.armed === u.id) { st.armed = null; clearTimeout(st.armTimer); retirer(u, true); } }}>{`Supprimer ${nom}`}</button>
            <button className="btn small" type="button" id={`del-no-${u.id}`} onClick={() => { disarm(); maj(); }}>Annuler</button>
          </div>
        )}
        {echec ? (
          <button type="button" className="btn small retry" onClick={() => reessayer(u)}>⚠ {nom} non enregistrée — touchez pour réessayer</button>
        ) : sv && sv.n > 0 ? (
          <span className="sauv">⏳ enregistrement…</span>
        ) : sv && sv.ok ? (
          <span className="sauv ok">✓ enregistrée</span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="cp" ref={refGarde}>
      <style>{CSS}</style>
      <Breadcrumbs items={[{ label: 'Commandes prêtes', onClick: () => navigate('/commande-prete') }, { label: dto.orderRef }]} />
      {/* v3 B : rendez-vous d'enlèvement + documents à imprimer reçus par le lien du mail (rien sinon). */}
      <RdvDocsCommande orderId={dto.id} imprimer={droits.imprimer} />
      {/* SPEC v3 D : sans « Encoder et envoyer », on VOIT la commande sans pouvoir la modifier — dit clairement. */}
      {lectureSeule && <p className="lecture" id="lectureSeule" role="status">🔒 {MSG_LECTURE_SEULE}</p>}
      {repris &&<p className="note">Commande déjà commencée : reprise là où elle en était.</p>}
      {avertCreation.testDemande !== null && avertCreation.testDemande !== testAffiche
        && avertCreation.liste.filter((a) => !(dto.avertissements || []).includes(a)).map((a) => <p className="note warn" key={a}>⚠ {a}</p>)}

      {/* Lecture seule : le fieldset désactive TOUT (champs, compteurs, photos, envoi, suppression) ; les liens restent. */}
      <fieldset className="screen" disabled={lectureSeule || envoiEnCours || st.supprEnCours}>
        {/* v2 : client (propositions) + commande de test — le reste de l'écran est inchangé. */}
        <div className="clientbox" id="clientBox">
          <ChoixClient id="cli" clients={clients} erreurListe={erreurClients} valeur={clientAffiche}
            onChoix={(c) => { if (c) changerClient(c); }} disabled={locked || dejaChargee || enteteTestFigee} />
          {clientAffiche
            ? <span className={'note' + (testAffiche ? ' warn' : '')} id="clientRegles">{texteRegles(infoClient, testAffiche)}</span>
            : <span className="note err" id="clientManque">{enteteTestFigee ? texteClientManqueTest(locked) : 'Choisissez le client de cette commande.'}</span>}
          {st.refusClient && <span className="note err" id="clientRefus">{st.refusClient}</span>}
          {st.lienExistantClient && <Link className="note" to={`/commande-prete/${st.lienExistantClient}`}>Ouvrir cette commande →</Link>}
          {admin ? (
            <label className="testbox" htmlFor="isTest">
              <input type="checkbox" id="isTest" checked={testAffiche} disabled={locked || (dejaChargee && !testAffiche)} onKeyDown={sansEntree}
                onChange={(ev) => changerTest(ev.target.checked)} />
              <span><b>Commande de test</b>{testAffiche && <BadgeTest style={{ marginLeft: 6 }} />} — mail seulement à l'adresse de test, rien vers EffiTrace</span>
            </label>
          ) : (testAffiche && <span id="testBadge"><BadgeTest /></span>)}
          {st.refusTest && <span className="note err" id="testRefus">{st.refusTest}</span>}
          {locked && <span className="note" id="clientLockNote">{texteVerrouEntete(dateHeure(dto.lockedAt), admin)}</span>}
          {!locked && dejaChargee && <span className="note" id="clientChargeNote">{texteDejaChargee(admin)}</span>}
          {/* (sans client : #clientManque le dit déjà) */}
          {!locked && enteteTestFigee && clientAffiche && <span className="note" id="testEnteteNote">{MSG_ENTETE_TEST_ADMIN}</span>}
        </div>

        <div className="row">
          <div className="field">
            <label htmlFor="ord">Réf. chargement (ODP)</label>
            <input id="ord" type="text" autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder="Scanner BROUT…"
              className={'mono' + (ordMsg ? ' bad' : '')} value={st.ord} disabled={locked || enteteTestFigee}
              onFocus={selectOnFocus} onMouseDown={keepSelection} onKeyDown={onEnterOrd}
              onChange={(ev) => { st.ord = ev.target.value; salirEntete(st, 'ord'); st.vuEntete.ord = false; delete st.refusEntete.ord; st.lienExistant = null; touched(); maj(); }}
              onBlur={() => onBlurEntete('ord')} />
            {ordMsg && <span className="note err">{ordMsg}</span>}
            {/* Avertissement BROUT : seulement une fois la réf. validée (Entrée/scan, sortie), jamais pendant la frappe. */}
            {!ordMsg && refInhabituelle(st.ord) && (st.vuEntete.ord || !enteteSale(st, 'ord')) && <span className="note warn" id="ordWarn">{MSG_REF_INHABITUELLE}</span>}
            {st.lienExistant && <Link className="note" to={`/commande-prete/${st.lienExistant}`}>Ouvrir cette commande →</Link>}
          </div>
        </div>
        {locked && <span className="note" id="lockNote">{`Réf. chargement verrouillée depuis l'envoi du ${dateHeure(dto.lockedAt)}.`}</span>}
        {st.sauvEntete.erreur && (
          <button type="button" className="btn small retry" onClick={() => { st.sauvEntete.erreur = null; enregistrerEntete(); }}>⚠ {st.sauvEntete.erreur} — touchez pour réessayer</button>
        )}

        <div className="counts">
          {[['PAL', 'npal', 'Palettes', 'palMinus', 'palPlus', 'Une palette de moins', 'Une palette de plus', nPal],
            ['COL', 'ncol', 'Colis', 'colMinus', 'colPlus', 'Un colis de moins', 'Un colis de plus', nCol]].map(([type, idc, lbl, idm, idp, am, ap, n]) => (
            <div className="stepper" key={type}>
              <span className="lbl" id={`l-${idc}`}>{lbl}</span>
              <div className="ctl">
                <button type="button" id={idm} aria-label={am} disabled={n === 0} onClick={() => plusMoins(type, -1)}>−</button>
                <input id={idc} type="text" inputMode="numeric" aria-labelledby={`l-${idc}`} autoComplete="off"
                  value={compteurAffiche(type)}
                  onFocus={selectOnFocus} onMouseDown={keepSelection} onKeyDown={(ev) => onEnterCompteur(ev, type)}
                  onChange={(ev) => { st.cnt[type] = ev.target.value; maj(); }}
                  onBlur={() => onBlurCompteur(type)} />
                <button type="button" id={idp} aria-label={ap} disabled={plein} onClick={() => plusMoins(type, 1)}>+</button>
              </div>
            </div>
          ))}
        </div>
        <span className={'note' + (countNote ? ' err' : '')} id="countNote" role="status" tabIndex={-1}>
          {Object.keys(st.cible).length ? '⏳ Mise à jour des fiches…' : (countNote || 'Zeus prépare une fiche par unité.')}
        </span>
        {st.countAsk && (
          <div className="confirm" id="countAsk" role="alert">
            <span id="countAskTxt" tabIndex={-1}>{texteQuestionCompteur(st, st.countAsk)}</span>
            <button className="btn small danger" type="button" id="countAskOk" onPointerDown={noterQuestion} onKeyDown={noterQuestion} onClick={ouiCompteur}>{`Oui, ${st.countAsk.n}`}</button>
            <button className="btn small" type="button" id="countAskNo" onPointerDown={noterQuestion} onKeyDown={noterQuestion} onClick={nonCompteur}>Annuler</button>
          </div>
        )}

        <div className="format">
          <div className="field">
            <label htmlFor="fmt">Format palette (pour toutes les palettes)</label>
            <select id="fmt" value={st.format} disabled={nPal === 0}
              onChange={(ev) => {
                st.format = ev.target.value; salirEntete(st, 'fmt'); delete st.refusEntete.fmt; touched(); maj();
                if (st.format !== 'AUTRE' || formatEtat(st).ok) enregistrerEntete('fmt');
              }}>
              <option value="EUR">120 × 80 cm (EUR)</option>
              <option value="IND">120 × 100 cm</option>
              <option value="AUTRE">Autre format…</option>
            </select>
          </div>
          {nPal > 0 && st.format === 'AUTRE' && (
            <div className="lw" id="fmtOther">
              {[['fl', 'Longueur (cm)'], ['fw', 'Largeur (cm)']].map(([k, lbl]) => (
                <div className="field" key={k}>
                  <label htmlFor={k}>{lbl}</label>
                  <input id={k} type="text" inputMode="numeric" autoComplete="off" value={st[k]} className={f.ok ? '' : 'bad'}
                    onFocus={selectOnFocus} onMouseDown={keepSelection} onKeyDown={(ev) => onEnterFormat(ev, k)}
                    onChange={(ev) => { st[k] = ev.target.value; salirEntete(st, 'fmt'); delete st.refusEntete.fmt; touched(); maj(); }}
                    onBlur={() => enregistrerEntete(k)} />
                </div>
              ))}
            </div>
          )}
          {nPal > 0 && st.format === 'AUTRE' && !f.ok && <span className="note err" id="fmtNote">{f.msg}</span>}
          {st.refusEntete.fmt && <span className="note err">{st.refusEntete.fmt}</span>}
          {nPal === 0 && <span className="note" id="fmtNone">Pas de palette dans cette commande : format non utilisé.</span>}
        </div>

        <div className="progress">
          <span id="progTxt" role="status" tabIndex={-1}>{`${done} / ${st.units.length} unités complètes`}</span>
          <span id="progPhotos" className="note">{`${nph} photo${nph > 1 ? 's' : ''}`}</span>
        </div>
        <div className="cards" id="cards">{st.units.map(fiche)}</div>
        {!st.units.length && <span className="note" id="cardsEmpty">Aucune unité. Indiquez le nombre de palettes ou de colis.</span>}
        <div className="adds">
          <button className="btn" type="button" id="addPal" disabled={plein} onClick={() => addAndFocus('PAL')}>＋ Palette</button>
          <button className="btn" type="button" id="addCol" disabled={plein} onClick={() => addAndFocus('COL')}>＋ Colis</button>
        </div>
        {plein && <span className="note" id="addsNote">{`Maximum ${MAX_UNITS} unités par commande atteint : supprimez une fiche avec ✕ pour en ajouter une autre.`}</span>}

        {e.dirty && <div className="modbanner" id="modBanner">{dto.isTest
          ? 'Modifié après l\'envoi : le mail de TEST montrait les anciennes valeurs. Envoyez la correction (adresse de test).'
          : `Modifié après l'envoi : ${(dto.client && dto.client.name) || 'le client'} a encore les anciennes valeurs. Envoyez la correction.`}</div>}
        {st.phase === 'confirm' && (
          <div className="sendbox" id="sendBox">
            <b id="sendRecap" tabIndex={-1}>{texteRecap(st, e.sent)}</b>
            {st.envoi.apercu ? (
              <>
                <span className="note">
                  Le mail part à <span className="mono" id="sendTo">{(st.envoi.apercu.to || []).join(', ')}</span>
                  {st.envoi.apercu.cc && st.envoi.apercu.cc.length ? <> (copie : <span className="mono">{st.envoi.apercu.cc.join(', ')}</span>)</> : null}.
                </span>
                <span className="note">Sujet : {st.envoi.apercu.subject}</span>
              </>
            ) : st.envoi.erreur ? (
              <span className="note err">{`Aperçu du mail impossible : ${st.envoi.erreur}`}</span>
            ) : <span className="note">Préparation du mail…</span>}
            <div className="row">
              <button className="btn primary" type="button" id="sendOk" disabled={!st.envoi.apercu || st.envoi.etat !== 'apercu' || enFile.current > 0} onClick={confirmer}>Confirmer l'envoi</button>
              <button className="btn" type="button" id="sendCancel" onClick={annulerEnvoi}>Annuler</button>
            </div>
          </div>
        )}
        <button className={'btn primary big' + (e.aria ? ' inactif' : '')} type="button" id="ready"
          disabled={e.disabled} aria-disabled={e.aria ? 'true' : undefined} onClick={clicPret}>{e.label}</button>
        {/* Lecture seule : pas les consignes de saisie (« le mail peut partir », « à compléter… ») — elles ne la concernent pas. */}
        <span className={'note' + (e.err && !lectureSeule ? ' err' : '')} id="readyNote" role="status" tabIndex={-1}>{lectureSeule ? NOTE_ENVOI_LECTURE_SEULE : e.note}</span>

        {st.message && (
          <div className={'msg' + (st.message.err ? ' err' : ' ok')} role="status">
            {st.message.txt}
            {st.message.liste && st.message.liste.length > 0 && <ul>{st.message.liste.map((x) => <li key={x}>{x}</li>)}</ul>}
          </div>
        )}
        {[...avertServeur, ...st.avertEnvoi].length > 0 && (
          <div className="msg warn" role="status">
            {[...avertServeur, ...st.avertEnvoi].map((a) => <div key={a}>⚠ {a}</div>)}
          </div>
        )}
        {(dto.status === 'ENVOI_EN_COURS' || dto.status === 'A_VERIFIER') && (
          <button type="button" className="btn small" onClick={rafraichir}>↻ Recharger l'état de la commande</button>
        )}

        {dto.mails && dto.mails.length > 0 && (
          <div className="histo">
            <span className="lbl">Envois</span>
            {dto.mails.map((m) => (
              <div className="histo-l" key={m.id}>
                <span>
                  {`${m.kind === 'UPDATE' ? 'Correction' : 'Mail'} du ${dateHeure(m.sentAt)} · ${m.sentByName || '—'} · `}
                  <b className={'etat-' + m.state}>{{ ENVOYE: 'envoyé', EN_COURS: 'en cours', A_VERIFIER: 'à vérifier', ECHEC: 'non parti' }[m.state] || m.state}</b>
                  {m.error && m.state !== 'ENVOYE' ? ` — ${m.error}` : ''}
                </span>
                {m.state === 'A_VERIFIER' && admin && (
                  <span className="row-btns">
                    <button type="button" className="btn small" onClick={() => resoudre(m, true)}>Marquer comme envoyé</button>
                    <button type="button" className="btn small" onClick={() => resoudre(m, false)}>Renvoyer</button>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* « Supprimer le brouillon » : seulement quand le serveur le permet ; question sur la page. */}
        {peutSupprimer(dto, admin, encoder) && (
          <div className="suppr" id="supprBox">
            {st.supprQuestion ? (
              <div className="confirm" role="alert">
                <span id="supprTxt">{texteConfirmationSuppression(dto)}</span>
                <button className="btn small danger" type="button" id="supprOk" onKeyDown={sansEntree} onClick={supprimerCommande}>
                  {st.supprEnCours ? '⏳ Suppression…' : 'Oui, supprimer'}
                </button>
                <button className="btn small" type="button" id="supprNon" onClick={annulerSuppressionCommande}>Annuler</button>
              </div>
            ) : (
              <button className="btn small" type="button" id="supprCommande" onKeyDown={sansEntree} onClick={demanderSuppressionCommande}>
                🗑 {libelleSuppression(dto)}
              </button>
            )}
          </div>
        )}
      </fieldset>
      <p className="note pied">{`Commande créée par ${dto.createdByName || '—'} le ${dateHeure(dto.createdAt)}.`}</p>
    </div>
  );
}

// Styles de l'écran (portée .cp) — repris de la maquette, en clair comme le reste de Zeus.
// Au doigt sur PDA : boutons ≥ 40 px, texte 16 px dans les champs, 390 px sans défilement horizontal.
const CSS = `
.cp{max-width:640px;margin:0 auto;color:#17232a;font-size:16px;line-height:1.45}
.cp *{box-sizing:border-box}
.cp .screen{background:#f7faf9;border:1px solid #cfd8dc;border-radius:14px;padding:12px;margin:0;display:flex;flex-direction:column;gap:12px;min-width:0}
.cp .clientbox{display:flex;flex-direction:column;gap:6px;padding:8px 10px;border-radius:10px;background:#fff;border:1px solid #cfd8dc}
.cp .testbox{display:flex;gap:8px;align-items:flex-start;font-size:.9rem;cursor:pointer;min-height:32px}
.cp .testbox input{width:22px;height:22px;margin:1px 0 0;flex:0 0 auto}
.cp .field{display:flex;flex-direction:column;gap:4px;min-width:0}
.cp .field label,.cp .lbl{font-size:.8rem;font-weight:700;color:#5b6b73;letter-spacing:.02em}
.cp input[type=text],.cp select{font:inherit;font-size:16px;color:#17232a;background:#fff;border:1px solid #cfd8dc;border-radius:8px;padding:8px 10px;width:100%;min-width:0;min-height:44px}
.cp .mono{font-family:"JetBrains Mono",Consolas,monospace}
.cp input.bad{border-color:#a6342b;background:#f8dedb}
.cp input:disabled,.cp select:disabled{opacity:.6;cursor:not-allowed;background:#e9eef0}
.cp input:focus-visible,.cp select:focus-visible,.cp button:focus-visible,.cp [tabindex]:focus-visible{outline:3px solid #0f6e74;outline-offset:2px}
.cp .row{display:flex;gap:8px;flex-wrap:wrap}
.cp .row>*{flex:1 1 160px;min-width:0}
.cp .btn{font:inherit;font-weight:700;border-radius:10px;border:1px solid #cfd8dc;background:#fff;color:#17232a;padding:10px 14px;cursor:pointer;min-height:44px;text-decoration:none;display:inline-flex;align-items:center;justify-content:center;gap:4px}
.cp .btn.primary{background:#0f6e74;border-color:#0f6e74;color:#fff}
.cp .btn.big{width:100%;padding:14px;font-size:1.05rem}
.cp .btn:disabled,.cp .btn.inactif{opacity:.45;cursor:not-allowed}
.cp .btn.small{padding:6px 10px;font-size:.9rem;font-weight:600;min-height:40px}
.cp .btn.danger{border-color:#a6342b;color:#a6342b;background:#f8dedb}
.cp .btn.retry{border-color:#a6342b;color:#a6342b;background:#fff;width:100%;white-space:normal;text-align:left}
.cp .btn.manque{outline:3px solid #965600;outline-offset:2px;background:#fbecd3;color:#965600}
@media (prefers-reduced-motion: no-preference){.cp .btn.manque{animation:cp-pulse 1.2s ease-in-out 3}}
@keyframes cp-pulse{50%{outline-color:transparent}}
.cp .note{font-size:.86rem;color:#5b6b73;overflow-wrap:anywhere}
.cp .note.err{color:#a6342b}
.cp .note.warn{color:#965600}
.cp .note.photo-msg{color:#965600;font-weight:700;background:#fbecd3;border-radius:8px;padding:6px 8px}
.cp .counts{display:grid;grid-template-columns:1fr 1fr;gap:8px}
@media (max-width:480px){.cp .counts{grid-template-columns:1fr}}
.cp .stepper{display:flex;flex-direction:column;gap:4px;padding:8px;border:1px solid #cfd8dc;border-radius:10px;background:#fff}
.cp .stepper .ctl{display:grid;grid-template-columns:44px minmax(3.5ch,1fr) 44px;gap:6px;align-items:center}
.cp .stepper button{font:inherit;font-weight:800;font-size:1.3rem;height:44px;min-height:44px;padding:0;border-radius:8px;border:1px solid #cfd8dc;background:#e9eef0;color:#17232a;cursor:pointer}
.cp .stepper button:disabled{opacity:.45;cursor:not-allowed}
.cp .stepper input{text-align:center;font-size:1.3rem;font-weight:700;padding:4px 2px}
.cp .format{display:flex;flex-direction:column;gap:6px;padding:8px 10px;border-radius:10px;background:#e9eef0}
.cp .format .lw{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.cp .cards{display:flex;flex-direction:column;gap:8px}
.cp .card{border:2px solid #cfd8dc;border-radius:12px;background:#fff;padding:10px;display:flex;flex-direction:column;gap:8px;min-width:0}
.cp .card.done{border-color:#2a7034}
.cp .card.todo{border-color:#965600}
.cp .card.bad{border-color:#a6342b}
.cp .card.nonsauv{border-style:dashed}
.cp .card-head{display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap}
.cp .state{font-size:.8rem;font-weight:700;padding:2px 8px;border-radius:12px;overflow-wrap:anywhere}
.cp .state.done{background:#dcefdc;color:#2a7034}
.cp .state.todo{background:#fbecd3;color:#965600}
.cp .state.bad{background:#f8dedb;color:#a6342b}
.cp .scanrow{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:6px}
.cp .vals{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
.cp .card-foot{display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap}
.cp .photos{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.cp .vignette{display:inline-flex;width:40px;height:40px;border-radius:6px;border:1px solid #cfd8dc;overflow:hidden;align-items:center;justify-content:center;background:#e9eef0}
.cp .vignette img{width:100%;height:100%;object-fit:cover;display:block}
.cp .fixed{font-size:.8rem;color:#5b6b73}
.cp .sauv{font-size:.8rem;color:#5b6b73}
.cp .sauv.ok{color:#2a7034}
.cp .confirm{display:flex;gap:8px;flex-wrap:wrap;align-items:center;padding:8px;border-radius:8px;background:#f8dedb;color:#a6342b;font-size:.9rem;font-weight:600}
.cp .confirm.warn{background:#fbecd3;color:#965600}
.cp .adds{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.cp .progress{display:flex;justify-content:space-between;align-items:center;font-weight:700;font-size:.92rem}
.cp .sendbox{display:flex;flex-direction:column;gap:8px;padding:10px;border-radius:10px;border:2px solid #0f6e74;background:#d7ecec}
.cp .modbanner{padding:8px;border-radius:8px;background:#fbecd3;color:#965600;font-size:.9rem;font-weight:600}
.cp .msg{padding:8px 10px;border-radius:8px;font-size:.9rem;overflow-wrap:anywhere}
.cp .msg.ok{background:#dcefdc;color:#2a7034}
.cp .msg.err{background:#f8dedb;color:#a6342b}
.cp .msg.warn{background:#fbecd3;color:#965600}
.cp .msg ul{margin:4px 0 0;padding-left:18px}
.cp .histo{display:flex;flex-direction:column;gap:6px;padding-top:6px;border-top:1px solid #cfd8dc;font-size:.88rem}
.cp .histo-l{display:flex;flex-direction:column;gap:4px}
.cp .row-btns{display:flex;gap:6px;flex-wrap:wrap}
.cp .etat-ENVOYE{color:#2a7034}.cp .etat-A_VERIFIER,.cp .etat-EN_COURS{color:#965600}.cp .etat-ECHEC{color:#a6342b}
.cp .pied{margin-top:8px}
.cp .lecture{margin:0 0 10px;padding:8px 10px;border-radius:8px;background:#fbecd3;color:#965600;font-size:.9rem;font-weight:600;overflow-wrap:anywhere}
.cp .suppr{display:flex;flex-direction:column;gap:6px;padding-top:6px;border-top:1px solid #cfd8dc}
.cp .suppr>.btn{align-self:flex-start}
`;
